/**
 * App-level toasts (SPEC § Toasts) — the first transient-feedback surface in the app.
 *
 * Bottom-centre, portaled to `document.body` above `--z-modal` so a toast raised from inside a
 * dialog is visible over it. The stack is capped at three, newest at the bottom: a user saving
 * several posts back to back gets one toast per click and the oldest simply falls off.
 *
 * Wording is never composed here — a success renders the server's `message` and its `warnings`
 * verbatim, an error the server's message (§12.5). This file owns placement, timing and dismissal.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { CloseIcon } from './icons';
import { cx } from './cx';

/** Beyond this the oldest is dropped — a stack, not a log. */
const MAX_TOASTS = 3;
export const TOAST_SUCCESS_MS = 5000;

export type ToastAction = { label: string; onClick: () => void };

export type ToastInput = {
  tone: 'success' | 'error';
  /** The server's sentence, rendered as-is. */
  message: string;
  /** Quieter second line — `warnings[]` of a save that partly degraded. */
  notes?: string[];
  link?: { href: string; label: string };
  actions?: ToastAction[];
  /** Omit for the tone default: successes auto-dismiss, errors never do. */
  autoDismissMs?: number | null;
};

type Toast = ToastInput & { id: number };

type ToastApi = {
  show: (toast: ToastInput) => number;
  dismiss: (id: number) => void;
};

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (api === null) throw new Error('useToast must be used inside <ToastProvider>');
  return api;
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const dismiss = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const show = useCallback((input: ToastInput) => {
    const id = nextId.current;
    nextId.current += 1;
    setToasts((current) => [...current, { ...input, id }].slice(-MAX_TOASTS));
    return id;
  }, []);

  // Escape dismisses the newest one. `Modal` owns Escape for the topmost dialog, so this listens in
  // the capture phase: a toast raised over a dialog has to answer before the dialog closes itself.
  useEffect(() => {
    if (toasts.length === 0) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const newest = toasts[toasts.length - 1];
      if (newest === undefined) return;
      event.stopPropagation();
      dismiss(newest.id);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [toasts, dismiss]);

  const api = useMemo<ToastApi>(() => ({ show, dismiss }), [show, dismiss]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {toasts.length > 0
        ? createPortal(
            <div className="toasts" role="region" aria-label="Notifications">
              {toasts.map((toast) => (
                <ToastCard key={toast.id} toast={toast} onDismiss={dismiss} />
              ))}
            </div>,
            document.body,
          )
        : null}
    </ToastContext.Provider>
  );
}

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss: (id: number) => void }) {
  const lifetime =
    toast.autoDismissMs === undefined
      ? toast.tone === 'success'
        ? TOAST_SUCCESS_MS
        : null
      : toast.autoDismissMs;

  useEffect(() => {
    if (lifetime === null) return;
    const timer = window.setTimeout(() => {
      onDismiss(toast.id);
    }, lifetime);
    return () => {
      window.clearTimeout(timer);
    };
  }, [lifetime, onDismiss, toast.id]);

  return (
    <div
      className={cx('toast', `toast--${toast.tone}`)}
      role={toast.tone === 'error' ? 'alert' : 'status'}
    >
      <div className="toast__body">
        <span className="toast__message">{toast.message}</span>
        {(toast.notes ?? []).map((note, index) => (
          <span className="toast__note" key={`${String(index)}-${note}`}>
            {note}
          </span>
        ))}
      </div>
      <div className="toast__actions">
        {toast.link !== undefined ? (
          <a
            className="toast__link"
            href={toast.link.href}
            target="_blank"
            rel="noreferrer noopener"
          >
            {toast.link.label}
          </a>
        ) : null}
        {(toast.actions ?? []).map((action) => (
          <button
            key={action.label}
            type="button"
            className="toast__action"
            onClick={() => {
              action.onClick();
              onDismiss(toast.id);
            }}
          >
            {action.label}
          </button>
        ))}
        <button
          type="button"
          className="toast__close"
          aria-label="Dismiss"
          onClick={() => {
            onDismiss(toast.id);
          }}
        >
          <CloseIcon size={12} />
        </button>
      </div>
    </div>
  );
}

export default ToastProvider;
